import { insightsIntl } from "../strings/intl";
import * as React from "react";
import { useLoaderData } from "react-router";
import { useAnalyticsContext } from "shared-utils";

import { Dimensions, MetricTypes } from "../autogen/metrics";
import { GenericAds } from "../generic-ads-screen";
import { type GenericAdsTabProps } from "../generic-ads-screen/components/generic-ads-tab";
import { type InsightsLoaderReturn } from "../loader";
import { channelMapping, channelsDataTooltipMap } from "../strings/tooltips";
import { getUrls } from "../urls";
import { allHistoricalData } from "../utils/custom-time-range-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { type DashboardOutput, type DataToFetch, getDashboardOutput } from "../utils/fetch-api";
import { addNotes, getNotes } from "../utils/notes";
import { type CustomTimeRange } from "../utils/types";

import {
  getDetailedChartFilters,
  getOverviewChart,
  getOverviewFilters,
  getSpendCpConversionRoasChart,
  getSummaryTable2,
  handleTabClick,
} from "./generic-ads";
import {
  AdPlatform,
  getCampaignPerformanceTableData2,
  getGoogleInsightsSummaryCards2,
  getInsightsOverviewChartData2,
  getSpendCpConversionRoasChartData2,
  getSummaryTableData2,
} from "./metrics-generic-ads";

const overviewTooltip = `
#### What is on this tab? \n\n
This page shows your overall Google ads performance. \n\n
The key three metrics reported on this page are spend, cost per conversion (Spend/conversions) and conversions.
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab daily to monitor the overall performance of your Google ads account \n\n
\n\n\n\n
#### Who should use this section? \n\n
Anyone who needs to monitor the overall performance of your Google ads account
`;

const campaignTooltip = `
#### What is on this tab? \n\n
This page shows your google ads performance breakdown down by campaign type. \n\n
The key metrics reported on this page are Spend, Cost per conversion (Spend/conversions) ,conversions and percent of budget spent on each campaign type over your selected timeframe
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab on weekly basis to monitor  performance broken down down by campaign type \n\n
\n\n\n\n
#### Who should use this section? \n\n
We designed this tab to be used by the individuals running your ad account
`;

const breakdownTooltip = `
#### What is on this tab? \n\n
This page shows your Google ads performance breakdown down by campaign.  \n\n
The key metrics reported on this page are Spend, Cost per conversion (Spend/conversions) and ROAS (Revenue/Spend)
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab on weekly basis to monitor the performance of each campaign  \n\n
\n\n\n\n
#### Who should use this section? \n\n
We designed this tab to be used by the individuals running your ad account  \n\n
\n\n\n\n
#### How should I interpret these metrics? \n\n
All metrics are reported using 7 day click or 1 day view attribution window \n\n
`;

const OVERVIEW_METRICS = [
  MetricTypes.GOOGLE_AD_SPEND,
  MetricTypes.GOOGLE_AD_COST_PER_CONVERSION,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS,
];

const OVERVIEW_CARDS_METRICS = [
  MetricTypes.GOOGLE_AD_SPEND,
  MetricTypes.GOOGLE_AD_COST_PER_CONVERSION,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS,
];

const PERFORMANCE_METRICS = [
  MetricTypes.GOOGLE_AD_SPEND,
  MetricTypes.GOOGLE_AD_CLICKS,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS,
];

/**
 * Entry point
 */
const GoogleAds = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath, notesPath } = getUrls(customerDomain);
  const [isLoading, setIsLoading] = React.useState(false);
  const [requiresDataFetch, setRequiresDataFetch] = React.useState(true);
  const [requiresDataComputation, setRequiresDataComputation] = React.useState(true);
  const [tabDataError, setTabDataError] = React.useState(null);

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const [overviewDataCurrentRaw, setOverviewDataCurrentRaw] = React.useState(null);
  const [overviewDataChartRaw, setOverviewDataChartRaw] = React.useState(null);
  const [performanceTableDataRaw, setPerformanceTableDataRaw] = React.useState(null);
  const [insightsCampaignDataRaw, setInsightsCampaignDataRaw] = React.useState(null);
  const [insightsCampaignDataByCampaignTypeRaw, setInsightsCampaignDataByCampaignTypeRaw] = React.useState(null);
  const [performanceTableData, setPerformanceTableData] = React.useState(null);

  const [overviewSummaryData, setOverviewSummaryData] = React.useState(null);
  const [overviewChartData, setOverviewChartData] = React.useState(null);
  const [campaignTabChartData, setCampaignTabChartData] = React.useState(null);
  const [campaignTabSummaryData, setCampaignTabSummaryData] = React.useState(null);

  const notesValue = React.useRef<string>("");

  const [selectedCurrentTimeRangeEnabled, setSelectedCurrentTimeRangeEnabled] = React.useState(true);
  const [selectedReferenceTimeRangeEnabled, setSelectedReferenceTimeRangeEnabled] = React.useState(true);

  const onChange = () => {
    setRequiresDataFetch(true);
    // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    scheduleDataComputation();
  };

  const {
    selectedTimeRange,
    handleTimeRangeChange,
    currentPreset,
    temporalResolution,
    selectedButtonIndex,
    hideWeekly,
    hideMonthly,
    handleTempResChange,
  } = useDateAndTemp(onChange, onChange);

  const isStaff = useAnalyticsContext().isStaff;

  const allData = React.useMemo(
    () => [overviewChartData, overviewSummaryData, campaignTabSummaryData, campaignTabChartData, performanceTableData],
    [campaignTabChartData, campaignTabSummaryData, overviewChartData, overviewSummaryData, performanceTableData]
  );

  const allRawData = React.useMemo(
    () => [
      performanceTableDataRaw,
      insightsCampaignDataRaw,
      insightsCampaignDataByCampaignTypeRaw,
      overviewDataCurrentRaw,
      overviewDataChartRaw,
    ],
    [
      performanceTableDataRaw,
      insightsCampaignDataRaw,
      insightsCampaignDataByCampaignTypeRaw,
      overviewDataCurrentRaw,
      overviewDataChartRaw,
    ]
  );

  const scheduleDataComputation = React.useCallback(() => {
    setRequiresDataComputation(true);
  }, []);

  const computeDerivedData = () => {
    if (!requiresDataComputation || requiresDataFetch || isLoading) {
      return;
    }

    const performanceData = getCampaignPerformanceTableData2(
      performanceTableDataRaw,
      PERFORMANCE_METRICS,
      AdPlatform.GOOGLE_ADS
    );
    setPerformanceTableData(performanceData);

    const campaignTabData = getSpendCpConversionRoasChartData2(
      insightsCampaignDataRaw,
      selectedTimeRange,
      temporalResolution,
      AdPlatform.GOOGLE_ADS
    );
    setCampaignTabChartData(campaignTabData);
    const summaryData = getSummaryTableData2(
      insightsCampaignDataByCampaignTypeRaw,
      OVERVIEW_METRICS,
      Dimensions.CAMPAIGN_TYPE,
      AdPlatform.GOOGLE_ADS
    );
    setCampaignTabSummaryData(summaryData);

    const overviewChartData = getInsightsOverviewChartData2(
      overviewDataChartRaw,
      selectedTimeRange,
      temporalResolution,
      AdPlatform.GOOGLE_ADS
    );
    setOverviewChartData(overviewChartData);
    const summaryCardsData = getGoogleInsightsSummaryCards2(overviewDataCurrentRaw, selectedTimeRange);
    setOverviewSummaryData(summaryCardsData);

    setRequiresDataComputation(false);
  };
  computeDerivedData();

  React.useEffect(() => {
    if (allRawData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoading(false);
    }
  }, [allRawData]);

  const fetchData = React.useCallback(
    (
      dataToFetch: DataToFetch[],
      timeRange: CustomTimeRange,
      callback: (res: DashboardOutput) => void,
      aggregateBy?: Dimensions
    ) => {
      getDashboardOutput(
        dashboardDataPath,
        dataToFetch,
        timeRange,
        callback,
        aggregateBy,
        setTabDataError,
        temporalResolution
      );
    },
    [dashboardDataPath, temporalResolution]
  );

  const fetchPerformanceData = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: PERFORMANCE_METRICS,
          filename: "google_insights_performance_table_ctr",
          groupBys: [Dimensions.CAMPAIGN_ID, Dimensions.CAMPAIGN, Dimensions.CAMPAIGN_TYPE],
        },
      ],
      selectedTimeRange,
      setPerformanceTableDataRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchCampaignChartData = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "google_insights_campaign_ctr",
          groupBys: [Dimensions.CAMPAIGN_TYPE],
        },
      ],
      selectedTimeRange,
      setInsightsCampaignDataRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchCampaignChartDataAggregatedByCampaign = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "google_insights_campaign_ctr",
          groupBys: [Dimensions.CAMPAIGN_TYPE],
        },
      ],
      selectedTimeRange,
      setInsightsCampaignDataByCampaignTypeRaw,
      Dimensions.CAMPAIGN_TYPE
    );
  }, [fetchData, selectedTimeRange]);

  const fetchOverviewDataCurrent = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_CARDS_METRICS,
          filename: "google_insights_ctr",
          groupBys: [],
        },
      ],
      selectedTimeRange,
      setOverviewDataCurrentRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchOverviewDataChart = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_CARDS_METRICS,
          filename: "google_insights_ctr",
          groupBys: [],
        },
      ],
      allHistoricalData(selectedTimeRange),
      setOverviewDataChartRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  React.useEffect(() => {
    if (requiresDataFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setCampaignTabSummaryData(null);
      setPerformanceTableData(null);
      setOverviewSummaryData(null);
      setCampaignTabChartData(null);
      setOverviewDataCurrentRaw(null);
      setOverviewDataChartRaw(null);
      setInsightsCampaignDataByCampaignTypeRaw(null);
      setPerformanceTableDataRaw(null);

      fetchPerformanceData();
      fetchOverviewDataCurrent();
      fetchOverviewDataChart();
      fetchCampaignChartData();
      fetchCampaignChartDataAggregatedByCampaign();

      setIsLoading(true);
      setRequiresDataFetch(false);
    }
  }, [
    requiresDataFetch,
    fetchPerformanceData,
    fetchOverviewDataCurrent,
    fetchOverviewDataChart,
    fetchCampaignChartData,
    fetchCampaignChartDataAggregatedByCampaign,
  ]);

  React.useEffect(() => {
    getNotes(notesPath, "google", (result: any) => {
      notesValue.current = result.content;
    });
  }, [notesPath]);

  let tabData: GenericAdsTabProps[] = [];

  if (allData.every((x) => x)) {
    const overviewFilters = getOverviewFilters(
      isLoading,
      hideWeekly,
      hideMonthly,
      setSelectedCurrentTimeRangeEnabled,
      setSelectedReferenceTimeRangeEnabled,
      selectedButtonIndex,
      selectedCurrentTimeRangeEnabled,
      selectedReferenceTimeRangeEnabled,
      handleTempResChange
    );

    const overviewSummary = {
      summaryCards: [
        {
          title: insightsIntl.formatMessage({ id: "Google Ads Spend", defaultMessage: "Google Ads Spend" }),
          value: overviewSummaryData.spend,
          tooltip: insightsIntl.formatMessage({
            id: "Google Ads marketing spend based on Google platform",
            defaultMessage: "Google Ads marketing spend based on Google platform",
          }),
        },
        {
          title: insightsIntl.formatMessage({ id: "Google Ads Conversions", defaultMessage: "Google Ads Conversions" }),
          value: overviewSummaryData.conversions,
          tooltip: insightsIntl.formatMessage({
            id: "Number of customers who interacted with your Google ads",
            defaultMessage: "Number of customers who interacted with your Google ads",
          }),
        },
        {
          title: insightsIntl.formatMessage({
            id: "Google Ads Cost per Conversion",
            defaultMessage: "Google Ads Cost per Conversion",
          }),
          value: overviewSummaryData.cc,
          tooltip: insightsIntl.formatMessage({
            id: "Google Ads Spend divided by Google Ads Conversions",
            defaultMessage: "Google Ads Spend divided by Google Ads Conversions",
          }),
        },
        {
          title: insightsIntl.formatMessage({
            id: "Google Ads Return on Ad Spend",
            defaultMessage: "Google Ads Return on Ad Spend",
          }),
          value: overviewSummaryData.roas,
          tooltip: insightsIntl.formatMessage({
            id: "Google Ads attributed sales divided by Google Ads Spend",
            defaultMessage: "Google Ads attributed sales divided by Google Ads Spend",
          }),
        },
      ],
    };

    const overviewChart = getOverviewChart(
      overviewChartData,
      selectedCurrentTimeRangeEnabled,
      selectedReferenceTimeRangeEnabled
    );

    const campaignFilters = getDetailedChartFilters(hideWeekly, hideMonthly, handleTempResChange, selectedButtonIndex);

    const campaignChart = getSpendCpConversionRoasChart(campaignTabChartData);

    const campaignTable = getSummaryTable2(campaignTabSummaryData);

    const getToolTipForData = (record, columnId) => {
      return channelsDataTooltipMap[channelMapping[record[columnId]]];
    };

    tabData = [
      {
        title: insightsIntl.formatMessage({ id: "Account level overview", defaultMessage: "Account level overview" }),
        summaryCards: overviewSummary,
        chartFilters: overviewFilters,
        chartData: overviewChart,
        tooltip: overviewTooltip,
      },
      {
        title: insightsIntl.formatMessage({ id: "Performance by Campaign", defaultMessage: "Performance by Campaign" }),
        summaryTable: { ...campaignTable, getToolTipForData },
        chartData: campaignChart,
        chartFilters: {
          ...campaignFilters,
          switchOneName: "campaign-switch-one",
          switchTwoName: "campaign-switch-two",
        },
        tooltip: campaignTooltip,
      },
      {
        title: insightsIntl.formatMessage({ id: "Campaign Breakdown", defaultMessage: "Campaign Breakdown" }),
        performanceTableData: {
          performanceBreakdownData: performanceTableData,
        },
        tooltip: breakdownTooltip,
      },
    ];
    // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    tabData = addNotes(isStaff, notesValue, notesPath, "google", tabData);
  }

  return (
    <GenericAds
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleTimeRangeChange(dateRangePreset as Parameters<typeof handleTimeRangeChange>[0], startDate, endDate),
      }}
      pageTitle="Google Ads"
      tabDataLoading={requiresDataComputation}
      tabDataError={tabDataError}
      tabData={tabData}
      onSelectTab={handleTabClick(setSelectedTabIndex)}
      selectedTabIndex={selectedTabIndex}
    />
  );
};

export default GoogleAds;
